import { createSHA256 } from 'hash-wasm'
import { gatewayRequest, gatewayRelayBytes } from '../api/gateway.js'
import { config } from '../config.js'
import { readStore, writeStore } from '../storage/db.js'
import { ASSET_URI_RE, scanAssetUris } from './document.js'
import { thumbhashFromBlob, thumbhashFromCanvas } from './media-warm.js'

const SMALL_UPLOAD_LIMIT = 100 * 1024 * 1024
const PART_SIZE = 16 * 1024 * 1024
const URL_REFRESH_SKEW = 60_000
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm'])
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'svg', 'avif'])
const SUPPORTED_MIME_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/webp', 'image/avif', 'image/svg+xml',
  'video/mp4', 'video/webm',
])
const IMAGE_VARIANT_SIZES = Object.freeze([256, 500, 1500])
const HASH_RE = /^[0-9a-f]{16}$/
// Videos join the local-forever mirror up to this size: playback stays
// streamed on first view, but replays on this device come from disk — and the
// mirrored bytes let the poster derive + upload, so other devices get a fast
// first frame from the CDN too. Bigger files stay stream-only.
const VIDEO_MIRROR_LIMIT = 64 * 1024 * 1024

function environmentReadOnlyError() {
  const error = new Error('environment_read_only')
  error.code = 'environment_read_only'
  return error
}

function isWebKitRuntime() {
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent
  return /AppleWebKit/i.test(userAgent) && !/(Chrome|Chromium|Edg)/i.test(userAgent)
}

async function localUploadPayload(file) {
  if (file.size > SMALL_UPLOAD_LIMIT) return {}
  // Safari/WebKit releases vary in IndexedDB Blob structured-clone support.
  // ArrayBuffer is reliable there and keeps offline/crash recovery intact.
  if (isWebKitRuntime()) return { localBytes: await file.arrayBuffer() }
  return { localBlob: file.slice(0, file.size, file.type) }
}

function normalizePreviewRef(value) {
  if (typeof value === 'string') {
    const uri = ASSET_URI_RE.exec(value)
    if (uri) return { hash: uri[1], ext: uri[2].toLowerCase() }
    return HASH_RE.test(value) ? { hash: value, ext: '' } : null
  }
  const hash = String(value?.hash || '')
  if (!HASH_RE.test(hash)) return null
  return { hash, ext: String(value?.ext || '').toLowerCase() }
}

async function createVideoPoster(source, timeoutMs = 5_000) {
  if (typeof document === 'undefined') return null
  const video = document.createElement('video')
  video.preload = 'auto'
  video.muted = true
  video.playsInline = true
  if (/^https:/i.test(source)) video.crossOrigin = 'anonymous'
  let timer = null
  const waitFor = (event, errorEvent = 'error') => new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener(event, onReady)
      video.removeEventListener(errorEvent, onError)
      if (timer != null) clearTimeout(timer)
    }
    const onReady = () => { cleanup(); resolve() }
    const onError = () => { cleanup(); reject(new Error('video_poster_unavailable')) }
    video.addEventListener(event, onReady, { once: true })
    video.addEventListener(errorEvent, onError, { once: true })
    timer = setTimeout(onError, timeoutMs)
  })
  try {
    const loaded = waitFor('loadeddata')
    video.src = source
    await loaded
    const duration = Number.isFinite(video.duration) ? video.duration : 0
    const target = Math.min(0.1, Math.max(0, duration - 0.05))
    if (target > 0 && Math.abs(video.currentTime - target) > 0.001) {
      const sought = waitFor('seeked')
      video.currentTime = target
      await sought
    }
    const naturalW = video.videoWidth || 0
    const naturalH = video.videoHeight || 0
    if (!naturalW || !naturalH) return null
    const scale = Math.min(1, 1500 / Math.max(naturalW, naturalH))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(naturalW * scale))
    canvas.height = Math.max(1, Math.round(naturalH * scale))
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) return null
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    const thumbhash = thumbhashFromCanvas(canvas)
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.86))
    if (!blob) return null
    return { url: URL.createObjectURL(blob), blob, duration, width: naturalW, height: naturalH, mime: blob.type || 'image/jpeg', thumbhash }
  } catch {
    return null
  } finally {
    if (timer != null) clearTimeout(timer)
    video.removeAttribute('src')
    try { video.load() } catch {}
  }
}

async function loadImageForCanvas(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' })
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() }
    } catch { /* SVG and older WebKit builds use the image-element fallback. */ }
  }
  if (typeof document === 'undefined') return null
  const url = URL.createObjectURL(blob)
  const image = new Image()
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve
      image.onerror = () => reject(new Error('image_variant_decode_failed'))
      image.src = url
    })
    if (!image.naturalWidth || !image.naturalHeight) return null
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) }
  } catch {
    URL.revokeObjectURL(url)
    return null
  }
}

async function createImageVariants(blob) {
  if (typeof document === 'undefined') return []
  const decoded = await loadImageForCanvas(blob)
  if (!decoded) return []
  try {
    const variants = []
    for (const size of IMAGE_VARIANT_SIZES) {
      const scale = Math.min(1, size / Math.max(decoded.width, decoded.height))
      const width = Math.max(1, Math.round(decoded.width * scale))
      const height = Math.max(1, Math.round(decoded.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d', { alpha: true })
      if (!context) continue
      context.drawImage(decoded.source, 0, 0, width, height)
      const output = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', size === 1500 ? 0.86 : 0.82))
      if (!output || output.type !== 'image/webp') continue
      variants.push({ kind: String(size), blob: output, width, height, mime: 'image/webp' })
    }
    return variants
  } finally {
    decoded.close?.()
  }
}

export function extensionFor(file) {
  const nameExt = String(file.name || '').match(/\.([A-Za-z0-9]{1,8})$/)?.[1]?.toLowerCase()
  if (nameExt) return nameExt === 'jpeg' ? 'jpg' : nameExt
  return ({ 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/avif': 'avif', 'image/svg+xml': 'svg', 'video/mp4': 'mp4', 'video/webm': 'webm' })[file.type] || 'bin'
}

export function isSupportedCanvasFile(file) {
  const mime = String(file?.type || '').toLowerCase().split(';', 1)[0].trim()
  const ext = extensionFor(file)
  return SUPPORTED_MIME_TYPES.has(mime) && (IMAGE_EXTENSIONS.has(ext) || VIDEO_EXTENSIONS.has(ext))
}

async function hashFileFallback(file) {
  const sha = await createSHA256()
  sha.init()
  for (let offset = 0; offset < file.size; offset += 4 * 1024 * 1024) {
    sha.update(new Uint8Array(await file.slice(offset, offset + 4 * 1024 * 1024).arrayBuffer()))
  }
  return sha.digest('hex').slice(0, 16)
}

function mergeAbortSignals(...candidates) {
  const signals = [...new Set(candidates.filter(Boolean))]
  if (signals.length <= 1) return { signal: signals[0], dispose() {} }
  const controller = new AbortController()
  const listeners = new Map()
  const abortFrom = signal => {
    if (!controller.signal.aborted) controller.abort(signal.reason)
  }
  for (const signal of signals) {
    if (signal.aborted) {
      abortFrom(signal)
      break
    }
    const listener = () => abortFrom(signal)
    listeners.set(signal, listener)
    signal.addEventListener('abort', listener, { once: true })
  }
  return {
    signal: controller.signal,
    dispose() {
      for (const [signal, listener] of listeners) signal.removeEventListener('abort', listener)
      listeners.clear()
    },
  }
}

export async function hashFile(file) {
  if (typeof Worker === 'undefined' || isWebKitRuntime()) return hashFileFallback(file)
  try {
    return await new Promise((resolve, reject) => {
      const worker = new Worker(new URL('../workers/hash-worker.js', import.meta.url), { type: 'module' })
      const timeout = setTimeout(() => { worker.terminate(); reject(new Error('asset_hash_worker_timeout')) }, 15_000)
      worker.onmessage = event => {
        clearTimeout(timeout)
        worker.terminate()
        event.data?.error ? reject(new Error(event.data.error)) : resolve(event.data.hash)
      }
      worker.onerror = event => {
        clearTimeout(timeout)
        worker.terminate()
        reject(event.error || new Error(event.message || 'asset_hash_failed'))
      }
      worker.postMessage({ file })
    })
  } catch {
    // Safari/WebKit releases vary in module Worker support. The fallback uses
    // the same incremental SHA-256 implementation and therefore produces the
    // exact same content-addressed asset URI.
    return hashFileFallback(file)
  }
}

async function uploadSingle(file, hash, request, signal) {
  const signed = await request('/sync/asset/presign-put', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, contentType: file.type || 'application/octet-stream' }),
  })
  const response = await fetch(signed.url, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file, signal })
  if (!response.ok) throw new Error(`asset_put_${response.status}`)
  await request('/sync/asset/commit', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, mime: file.type || 'application/octet-stream' }),
  })
}

async function uploadVariant(variant, hash, request, signal) {
  const signed = await request('/sync/asset/variant/presign-put', {
    method: 'POST', signal,
    body: JSON.stringify({ hash, kind: variant.kind, bytes: variant.blob.size, mime: variant.mime }),
  })
  const response = await fetch(signed.url, {
    method: 'PUT',
    headers: { 'Content-Type': variant.mime },
    body: variant.blob,
    signal,
  })
  if (!response.ok) throw new Error(`asset_variant_put_${response.status}`)
  return request('/sync/asset/variant/commit', {
    method: 'POST', signal,
    body: JSON.stringify({ hash, kind: variant.kind, bytes: variant.blob.size, mime: variant.mime }),
  })
}

async function uploadMultipart(file, hash, request, onSession, signal) {
  const start = await request('/sync/asset/multipart/start', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, mime: file.type || 'application/octet-stream', partSize: PART_SIZE }),
  })
  await onSession(start)
  const count = Math.ceil(file.size / start.partSize)
  const numbers = Array.from({ length: count }, (_, index) => index + 1)
  const signed = await request('/sync/asset/multipart/parts', {
    method: 'POST', signal, body: JSON.stringify({ hash, uploadId: start.uploadId, partNumbers: numbers }),
  })
  const completed = []
  let cursor = 0
  async function worker() {
    while (cursor < numbers.length) {
      const partNumber = numbers[cursor++]
      const begin = (partNumber - 1) * start.partSize
      const response = await fetch(signed.parts[String(partNumber)], { method: 'PUT', body: file.slice(begin, begin + start.partSize), signal })
      if (!response.ok) throw new Error(`multipart_part_${response.status}`)
      completed.push({ partNumber, etag: response.headers.get('etag') })
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, count) }, worker))
  await request('/sync/asset/multipart/complete', {
    method: 'POST', signal, body: JSON.stringify({ hash, uploadId: start.uploadId, parts: completed.sort((a, b) => a.partNumber - b.partNumber) }),
  })
}

export class AssetMap extends EventTarget {
  #entries = new Map()
  #pending = new Map()
  #localFiles = new Map()
  #controllers = new Map()
  #uncommitted = new Set()
  #posters = new Map()
  #posterPending = new Map()
  #warms = new Map()
  #warmPending = new Map()
  #derivationPending = new Map()
  #derivationQueue = []
  #derivedOnce = new Set()
  #mirrorQueue = []
  #mirrorActive = 0
  #mirrorTimer = null
  #mirrorSeen = new Set()
  #derivationQueued = new Set()
  #derivationActive = 0
  #derivationIdleHandle = 0
  #refreshTimers = new Map()
  #disposed = false
  #lifecycleController = new AbortController()

  constructor(options = {}) {
    super()
    const request = options.request || gatewayRequest
    this.request = async (path, requestOptions = {}) => {
      this.#assertActive()
      const merged = mergeAbortSignals(requestOptions.signal, this.#lifecycleController.signal)
      try {
        const result = await request(path, { ...requestOptions, signal: merged.signal })
        this.#assertActive()
        return result
      } finally {
        merged.dispose()
      }
    }
    this.read = options.read || readStore
    this.write = options.write || writeStore
    this.hash = options.hash || hashFile
    this.createWarm = options.createWarm || thumbhashFromBlob
    this.createPoster = options.createPoster || createVideoPoster
    this.createVariants = options.createVariants || createImageVariants
    // Test seam: the default idle fallback timer is unref'd (must not keep a
    // node process alive), which also means it cannot drive a test's event
    // loop — tests inject a ref'd scheduler instead.
    this.scheduleIdle = options.scheduleIdle || null
    const fetchRemoteBytes = options.fetchRemoteBytes || gatewayRelayBytes
    this.fetchRemoteBytes = url => {
      this.#assertActive()
      return fetchRemoteBytes(url, { signal: this.#lifecycleController.signal })
    }
    this.readOnly = options.readOnly === true
    this.assetCdnBase = options.assetCdnBase !== undefined ? options.assetCdnBase : config.assetCdnBase
    this.mirrorDelay = options.mirrorDelay ?? 10_000
  }

  #assertActive() {
    if (!this.#disposed) return
    const error = new Error('asset_map_disposed')
    error.code = 'asset_map_disposed'
    throw error
  }

  // With an R2 custom domain configured, derived variants live on stable,
  // CDN-cached capability URLs (same object path, public host, no expiring
  // query) — the browser caches the bytes forever and presign rotation stops
  // invalidating previews. Originals keep their presigned URLs.
  #rewriteVariantsToCdn(variants) {
    const base = this.assetCdnBase
    if (!base || !variants) return variants
    const rewritten = {}
    for (const [kind, variant] of Object.entries(variants)) {
      if (!variant?.url) { rewritten[kind] = variant; continue }
      try {
        const parsed = new URL(variant.url)
        // ?cv= busts caches keyed on the bare path (bump when previously-cached
        // responses must be invalidated for every client, e.g. the 2026-08 window
        // where edge/browser caches pinned pre-upload 404s for 30 days).
        rewritten[kind] = /^\/[^/]+\/variants\//.test(parsed.pathname)
          ? { ...variant, url: `${base}${parsed.pathname}?cv=2`, expiresAt: null }
          : variant
      } catch {
        rewritten[kind] = variant
      }
    }
    return rewritten
  }

  // Originals are content-addressed capability URLs exactly like variants
  // ({uid}/{hash}, unguessable). With the CDN host configured they are served
  // from the edge (cached, CORS-enabled, reachable where the raw R2 endpoint
  // is not) instead of an hourly-rotating presigned URL — canvas full-res
  // loads, video streaming and mirroring all ride the fast path.
  #rewriteOriginalToCdn(item) {
    const base = this.assetCdnBase
    if (!base || !item?.url) return item
    try {
      const parsed = new URL(item.url)
      if (!/^\/[^/]+\/[0-9a-f]{16}$/.test(parsed.pathname)) return item
      return { ...item, url: `${base}${parsed.pathname}`, expiresAt: null }
    } catch {
      return item
    }
  }

  #rememberWarm(hash, uri, thumbhash) {
    const value = typeof thumbhash === 'string' ? thumbhash.trim() : ''
    if (!HASH_RE.test(String(hash)) || !value) return null
    const previous = this.#warms.get(hash)
    this.#warms.set(hash, value)
    if (previous !== value) {
      this.dispatchEvent(new CustomEvent('warm-ready', {
        detail: { hash, uri, thumbhash: value },
      }))
    }
    return value
  }

  async #createWarm(blob) {
    try { return await this.createWarm(blob) } catch { return null }
  }

  #cacheRemoteEntry(item, fallbackExt = '') {
    const hash = String(item?.hash || '')
    if (!HASH_RE.test(hash) || !item?.url) return null
    const previous = this.#entries.get(hash)
    if (previous?.local) {
      // Local objectURLs are authoritative — a remote capability must never
      // downgrade them back to network URLs. It may only fill gaps the local
      // row lacks (e.g. CDN variants that were never derived on this device).
      // The cloud URL is still recorded on the side (remoteUrl): generation
      // providers can only fetch http(s), never this device's objectURLs.
      const mapped = this.#rewriteOriginalToCdn(item)
      const merged = {
        ...previous,
        remoteUrl: mapped.url,
        remoteExpiresAt: mapped.expiresAt || null,
        variants: { ...this.#rewriteVariantsToCdn(item.variants || {}), ...(previous.variants || {}) },
      }
      this.#entries.set(hash, merged)
      return merged
    }
    const mapped = this.#rewriteOriginalToCdn(item)
    const entry = {
      ...(previous || {}),
      ...mapped,
      hash,
      ext: String(item.ext || fallbackExt || previous?.ext || '').toLowerCase(),
      local: false,
      variants: { ...(previous?.variants || {}), ...this.#rewriteVariantsToCdn(item.variants || {}) },
    }
    this.#entries.set(hash, entry)
    this.#scheduleRefresh(entry)
    if (previous?.url && previous.url !== entry.url) {
      this.dispatchEvent(new CustomEvent('asset-url-refreshed', {
        detail: { hash, uri: `asset://${hash}.${entry.ext || 'bin'}`, previousUrl: previous.url, url: entry.url, expiresAt: entry.expiresAt || null },
      }))
    }
    return entry
  }

  #scheduleRefresh(entry) {
    if (this.#disposed) return
    const hash = String(entry?.hash || '')
    const previous = this.#refreshTimers.get(hash)
    if (previous) clearTimeout(previous)
    this.#refreshTimers.delete(hash)
    if (!/^https?:/i.test(String(entry?.url || '')) || !(Number(entry?.expiresAt) > 0)) return
    const delay = Math.max(1_000, Number(entry.expiresAt) - Date.now() - URL_REFRESH_SKEW)
    const timer = setTimeout(() => {
      this.#refreshTimers.delete(hash)
      void this.retry(`asset://${hash}.${entry.ext || 'bin'}`, { force: true }).catch(() => {})
    }, delay)
    timer.unref?.()
    this.#refreshTimers.set(hash, timer)
  }

  async #restoreLocal(hash, ext) {
    this.#assertActive()
    if (this.#localFiles.has(hash)) return this.#localFiles.get(hash)
    const upload = await this.read('asset_uploads', hash)
    let blob = upload?.localBlob
    if (!(blob instanceof Blob) && upload?.localBytes instanceof ArrayBuffer) {
      blob = new Blob([upload.localBytes], { type: upload.mime || 'application/octet-stream' })
    }
    if (!(blob instanceof Blob)) return null
    const file = typeof File === 'function'
      ? new File([blob], upload.name || `asset.${upload.ext || ext}`, { type: upload.mime || blob.type })
      : blob
    this.#localFiles.set(hash, file)
    // Mirrored rows (status 'committed') already live in the cloud — marking
    // them uncommitted would make commitState() park saves as 'uploading'.
    if (upload.status !== 'committed') this.#uncommitted.add(hash)
    const previous = this.#entries.get(hash)
    if (!previous?.local) {
      this.#entries.set(hash, { hash, ext: upload.ext || ext, mime: upload.mime || blob.type || null, url: URL.createObjectURL(file), expiresAt: null, local: true })
    }
    const restored = this.#entries.get(hash)
    if (restored && upload.previewVariants && !restored.variants) {
      const variants = {}
      for (const [kind, preview] of Object.entries(upload.previewVariants)) {
        if (!(preview?.blob instanceof Blob)) continue
        variants[kind] = { url: URL.createObjectURL(preview.blob), blob: preview.blob, width: preview.width, height: preview.height, mime: preview.mime, local: true }
      }
      if (Object.keys(variants).length) {
        restored.variants = variants
        this.#entries.set(hash, restored)
        this.#derivedOnce.add(hash)
      }
    }
    // A paid result can crash after the atomic transaction commits but before
    // canvas-entry gets to activate its background work. The durable marker is
    // written only by that transaction, so reload may safely resume here; user
    // imports never carry it and retain their existing retry behavior.
    if (upload.deferredUpload === true && upload.status !== 'committed') {
      queueMicrotask(() => {
        if (!this.#disposed) void this.commitPreparedImport(upload).catch(() => {})
      })
    }
    return file
  }

  #queueMirror(hash) {
    if (this.readOnly || this.#disposed) return
    if (this.#mirrorSeen.has(hash) || !HASH_RE.test(String(hash))) return
    const entry = this.#entries.get(hash)
    if (!entry?.url || entry.local || this.#localFiles.has(hash)) return
    const ext = String(entry.ext || '').toLowerCase()
    const smallVideo = VIDEO_EXTENSIONS.has(ext) && Number(entry.bytes) > 0 && Number(entry.bytes) <= VIDEO_MIRROR_LIMIT
    if (!IMAGE_EXTENSIONS.has(ext) && !smallVideo) return
    this.#mirrorSeen.add(hash)
    this.#mirrorQueue.push(hash)
    // Deferred start: mirroring is background durability work — during the
    // first seconds after open it would compete with the canvas's own
    // first-paint texture downloads for the same (often thin) pipe.
    if (this.#mirrorTimer == null) {
      this.#mirrorTimer = setTimeout(() => {
        this.#mirrorTimer = null
        if (!this.#disposed) this.#pumpMirrors()
      }, this.mirrorDelay)
      this.#mirrorTimer?.unref?.()
    }
  }

  // CDN URLs are CORS-enabled and edge-cached — fetch them directly and keep
  // the authenticated gateway relay as the fallback when the edge is
  // unreachable (the relay allowlists the CDN host for exactly this case).
  async #fetchMirrorBytes(url) {
    this.#assertActive()
    if (this.assetCdnBase && String(url).startsWith(this.assetCdnBase)) {
      try {
        const response = await fetch(url, { signal: this.#lifecycleController.signal })
        if (response.ok) return await response.blob()
      } catch { /* fall through to the relay */ }
    }
    return this.fetchRemoteBytes(url)
  }

  // A small pool instead of a single chain: relay downloads are RTT-bound, so
  // a big canvas took dozens of sequential round trips and rarely finished
  // mirroring within one session — every open stayed slow. Three concurrent
  // transfers converge ~3× faster while still leaving bandwidth headroom for
  // the canvas's own first-paint texture loads.
  #pumpMirrors() {
    if (this.#disposed) return
    while (this.#mirrorActive < 3 && this.#mirrorQueue.length) {
      // Images first: they are small and unblock texture locality quickly; a
      // multi-MB video should never hold a slot while images wait behind it.
      const imageIndex = this.#mirrorQueue.findIndex(queued => IMAGE_EXTENSIONS.has(String(this.#entries.get(queued)?.ext || '').toLowerCase()))
      const hash = this.#mirrorQueue.splice(imageIndex >= 0 ? imageIndex : 0, 1)[0]
      this.#mirrorActive += 1
      void this.#mirrorRemote(hash)
        .catch(() => {})
        .finally(() => {
          this.#mirrorActive -= 1
          if (!this.#disposed) this.#pumpMirrors()
        })
    }
  }

  async #mirrorRemote(hash) {
    this.#assertActive()
    const entry = this.#entries.get(hash)
    if (!entry?.url || entry.local || this.#localFiles.has(hash)) return
    const ext = String(entry.ext || 'png').toLowerCase()
    const uri = `asset://${hash}.${ext}`
    const url = (await this.resolveFresh(uri)) || entry.url
    const blob = await this.#fetchMirrorBytes(url)
    this.#assertActive()
    const file = typeof File === 'function'
      ? new File([blob], `asset.${ext}`, { type: blob.type || entry.mime || 'image/png' })
      : blob
    const previous = await this.read('asset_uploads', hash).catch(() => null)
    await this.write('asset_uploads', {
      ...(previous || {}),
      hash, ext, mime: file.type, bytes: file.size,
      status: 'committed',
      ...(await localUploadPayload(file)),
      updatedAt: Date.now(),
    })
    this.#localFiles.set(hash, file)
    const current = this.#entries.get(hash) || entry
    const previousUrl = current.url
    const objectUrl = URL.createObjectURL(file)
    this.#entries.set(hash, { ...current, url: objectUrl, expiresAt: null, local: true })
    this.dispatchEvent(new CustomEvent('asset-url-refreshed', {
      detail: { hash, uri, previousUrl, url: objectUrl, expiresAt: null },
    }))
    // Now that bytes are local, derive (and upload) the preview variants that
    // legacy/desktop-synced assets never had — this is what populates the CDN.
    this.#derivedOnce.delete(hash)
    this.#queueDerived(hash, ext)
  }

  #trackUpload(file, hash, ext, existing, staged = false) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    try { this.#assertActive() } catch (error) { return Promise.reject(error) }
    const current = this.#pending.get(hash)
    if (current) return current
    const task = this.#upload(file, hash, ext, existing, staged)
    this.#pending.set(hash, task)
    // Imports are intentionally local-first. Keep a handled promise here so a
    // missing JWT or temporary network failure cannot become an unhandled
    // rejection or remove the item that is already visible on the Canvas.
    void task.catch(() => {}).finally(() => {
      if (this.#pending.get(hash) === task) this.#pending.delete(hash)
    })
    return task
  }

  async prime(document) {
    this.#assertActive()
    const assets = scanAssetUris(document)
    if (!assets.length) return
    const unresolved = []
    for (const asset of assets) {
      // Restore before trusting an existing entry: a mount's forced remote
      // resolve may have raced a non-local entry in here, and local bytes must
      // reclaim it (restoreLocal replaces non-local entries).
      const existing = this.#entries.get(asset.hash)
      if (existing?.local || await this.#restoreLocal(asset.hash, asset.ext) || existing) continue
      unresolved.push(asset)
    }
    for (let offset = 0; offset < unresolved.length; offset += 200) {
      const batch = unresolved.slice(offset, offset + 200)
      try {
        const result = await this.request('/sync/asset/resolve', {
          method: 'POST', body: JSON.stringify({ assets: batch.map(({ hash, ext }) => ({ hash, ext })), variants: ['500', 'poster'] }),
        })
        const refsByHash = new Map(batch.map(asset => [asset.hash, asset.ext]))
        for (const item of result.assets || []) this.#cacheRemoteEntry(item, refsByHash.get(item.hash))
      } catch {
        // A local working copy remains openable while signed out or offline.
        // Missing remote assets render as unavailable until retry succeeds.
      }
    }
    // Content-addressed bytes never change: mirror this canvas's remote image
    // originals into local storage once, then this device never re-downloads
    // them (and presign rotation stops mattering). Videos stay streamed.
    for (const asset of assets) this.#queueMirror(asset.hash)
    // Opening a Canvas only resolves capabilities. Historical variant
    // backfill belongs to the project-list preview path below; doing it here
    // would download and decode every original while first paint is still
    // competing for the same network, CPU and memory budget.
  }

  resolve(uri) {
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return uri || null
    const entry = this.#entries.get(match[1])
    if (!entry) return null
    if (entry.expiresAt && entry.expiresAt - Date.now() < URL_REFRESH_SKEW && !this.#pending.has(match[1])) {
      void this.retry(uri, { force: true }).catch(() => {})
    }
    return entry.url
  }

  resolveVariant(uri, kind) {
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return null
    const entry = this.#entries.get(match[1])
    const variant = this.#freshVariant(entry, String(kind || ''))
    if (!variant && entry?.variants?.[String(kind || '')] && !this.#pending.has(match[1])) {
      void this.retry(uri, { force: true }).catch(() => {})
    } else if (!variant && entry && !this.#derivedOnce.has(match[1])) {
      // Nothing recorded for this asset yet (fresh session, offline import):
      // derive previews lazily on first demand instead of never.
      this.#queueDerived(match[1], entry.ext)
    }
    return variant?.url || null
  }

  async resolveFresh(uri, kind = null) {
    this.#assertActive()
    const value = String(uri || '')
    const match = ASSET_URI_RE.exec(value)
    if (!match) return value || null
    let entry = this.#entries.get(match[1])
    if (!entry?.local) {
      // Local-first, always: bytes this device already holds are authoritative
      // and instant. Sprites mount before prime()'s restore pass, and their
      // forced remote resolve used to win that race — the entry came back
      // remote, prime saw it "fresh" and skipped the restore, and every reopen
      // re-downloaded the whole canvas over the network.
      try { await this.#restoreLocal(match[1], match[2].toLowerCase()) } catch { /* fall through to remote */ }
      entry = this.#entries.get(match[1])
    }
    const freshOriginal = entry?.url && (!entry.expiresAt || entry.expiresAt - Date.now() >= URL_REFRESH_SKEW)
    const freshVariant = kind ? this.#freshVariant(entry, String(kind)) : null
    if (freshVariant?.url) return freshVariant.url
    if (freshOriginal && !kind) return entry.url
    if (!freshOriginal || (kind && entry?.variants?.[String(kind)])) {
      try { await this.retry(value, { force: true }) } catch {}
      entry = this.#entries.get(match[1])
    }
    return (kind ? this.#freshVariant(entry, String(kind))?.url : null) || entry?.url || null
  }

  // Generation providers fetch sources over http(s) — this device's local
  // objectURLs (authoritative for rendering since the local-forever change)
  // are unusable there. Committed assets always have a cloud copy; resolve its
  // capability URL directly from the gateway WITHOUT touching the local entry
  // (the plain resolve path would be blocked by #cacheRemoteEntry's
  // no-downgrade guard, which is exactly why entry.url can't serve here).
  async resolveApiUrl(uri) {
    this.#assertActive()
    const value = String(uri || '')
    const match = ASSET_URI_RE.exec(value)
    const httpFresh = (url, expiresAt) => Boolean(url) && /^https?:/i.test(url)
      && (!expiresAt || expiresAt - Date.now() >= URL_REFRESH_SKEW)
    if (!match) return httpFresh(value, null) ? value : null
    const hash = match[1]
    const entry = this.#entries.get(hash)
    if (entry && !entry.local && httpFresh(entry.url, entry.expiresAt)) return entry.url
    if (entry && httpFresh(entry.remoteUrl, entry.remoteExpiresAt)) return entry.remoteUrl
    const ext = entry?.ext || match[2].toLowerCase()
    const result = await this.request('/sync/asset/resolve', {
      method: 'POST', body: JSON.stringify({ assets: [{ hash, ext }], variants: [] }),
    })
    const item = (result.assets || []).find(asset => asset.hash === hash)
    if (!item?.url) return null
    const mapped = this.#rewriteOriginalToCdn(item)
    const target = this.#entries.get(hash)
    if (target) {
      this.#entries.set(hash, { ...target, remoteUrl: mapped.url, remoteExpiresAt: mapped.expiresAt || null })
    }
    return httpFresh(mapped.url, mapped.expiresAt || null) ? mapped.url : null
  }

  async refreshByUrl(url) {
    this.#assertActive()
    const value = String(url || '')
    if (!value) return null
    for (const [hash, entry] of this.#entries) {
      const variantKind = Object.entries(entry?.variants || {}).find(([, variant]) => variant?.url === value)?.[0] || null
      if (entry?.url !== value && !variantKind) continue
      const uri = `asset://${hash}.${entry.ext || 'bin'}`
      try { await this.retry(uri, { force: true }) } catch { return null }
      const refreshed = this.#entries.get(hash)
      return {
        hash,
        uri,
        kind: variantKind,
        url: variantKind ? this.#freshVariant(refreshed, variantKind)?.url || null : refreshed?.url || null,
      }
    }
    return null
  }

  async resolveMedia(uri) {
    this.#assertActive()
    const value = String(uri || '')
    const match = ASSET_URI_RE.exec(value)
    if (match && !VIDEO_EXTENSIONS.has(match[2].toLowerCase())) throw new Error('web_video_format_unsupported')
    let url = value
    if (match) {
      let entry = this.#entries.get(match[1])
      if (!entry) {
        await this.#restoreLocal(match[1], match[2].toLowerCase())
        entry = this.#entries.get(match[1])
      }
      const needsRefresh = !entry?.url || (!entry.local && entry.expiresAt && entry.expiresAt - Date.now() < URL_REFRESH_SKEW)
      url = needsRefresh ? await this.retry(value, { force: true }) : entry.url
    }
    if (!url || !/^(?:blob:|https?:)/i.test(url)) throw new Error('web_media_url_unsupported')
    return url
  }

  async resolveHashes(refs) {
    this.#assertActive()
    const normalized = []
    const seen = new Set()
    for (const value of refs || []) {
      const ref = normalizePreviewRef(value)
      if (!ref || seen.has(ref.hash)) continue
      seen.add(ref.hash)
      normalized.push(ref)
    }
    if (!normalized.length) return []

    const unresolved = []
    for (const ref of normalized) {
      const entry = this.#entries.get(ref.hash)
      const fresh = entry && (!entry.expiresAt || entry.expiresAt - Date.now() >= URL_REFRESH_SKEW)
      // Local-first even for ext-less refs (cloud list rows carry ext ''):
      // #restoreLocal reads the asset_uploads row and prefers the ext recorded
      // there, so locally stored bytes serve previews without a presign.
      if (entry?.local || await this.#restoreLocal(ref.hash, ref.ext) || fresh) continue
      // Disk-cached capability from a previous visit: re-entering the list
      // page must not wait a gateway presign round trip (plus a possible
      // Railway cold start) for URLs that are still fresh.
      try {
        const cached = await this.read('remote_media_cache', ref.hash)
        if (cached?.item?.url && (Number(cached.item.expiresAt) || 0) - Date.now() >= URL_REFRESH_SKEW) {
          this.#cacheRemoteEntry(cached.item, ref.ext)
          continue
        }
      } catch { /* cache miss/corruption → resolve over the network */ }
      unresolved.push(ref)
    }

    for (let offset = 0; offset < unresolved.length; offset += 200) {
      const batch = unresolved.slice(offset, offset + 200)
      const result = await this.request('/sync/asset/resolve', {
        method: 'POST', body: JSON.stringify({ assets: batch, variants: ['500', 'poster'] }),
      })
      const refsByHash = new Map(batch.map(ref => [ref.hash, ref]))
      for (const item of result.assets || []) {
        const ref = refsByHash.get(item.hash)
        this.#cacheRemoteEntry(item, ref?.ext)
        void this.write('remote_media_cache', { hash: item.hash, item, updatedAt: Date.now() }).catch(() => {})
      }
    }

    const missingVideoPosters = normalized.filter(ref => {
      const entry = this.#entries.get(ref.hash)
      const isVideo = String(entry?.mime || '').startsWith('video/') || VIDEO_EXTENSIONS.has(ref.ext)
      return isVideo && !this.#freshVariant(entry, 'poster') && !this.#posters.has(ref.hash)
    })
    for (const ref of missingVideoPosters) this.#queueDerived(ref.hash, ref.ext || 'mp4')

    for (const ref of normalized) {
      const entry = this.#entries.get(ref.hash)
      const isImage = String(entry?.mime || '').startsWith('image/') || IMAGE_EXTENSIONS.has(ref.ext)
      if (isImage && !this.#freshVariant(entry, '500')) this.#queueDerived(ref.hash, ref.ext)
    }

    return normalized.map(ref => {
      const entry = this.#entries.get(ref.hash)
      if (!entry?.url) return null
      const isVideo = String(entry.mime || '').startsWith('video/') || VIDEO_EXTENSIONS.has(ref.ext)
      const remotePreview = this.#freshVariant(entry, isVideo ? 'poster' : '500')
      const localPoster = isVideo ? this.#posters.get(ref.hash) : null
      const preview = remotePreview || localPoster
      return {
        hash: ref.hash,
        ext: preview ? (isVideo ? 'jpg' : 'webp') : entry.ext || ref.ext || '',
        mime: preview?.mime || entry.mime || null,
        url: preview?.url || entry.url,
        originalUrl: entry.url,
        previewKind: preview ? (isVideo ? 'poster' : '500') : 'original',
        expiresAt: preview?.expiresAt || entry.expiresAt || null,
      }
    }).filter(Boolean)
  }

  #freshVariant(entry, kind) {
    const variant = entry?.variants?.[kind]
    if (!variant?.url) return null
    if (variant.expiresAt && variant.expiresAt - Date.now() < URL_REFRESH_SKEW) return null
    return variant
  }

  resolvePoster(uri) {
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return null
    return this.#posters.get(match[1])?.url || this.#freshVariant(this.#entries.get(match[1]), 'poster')?.url || null
  }

  resolveWarm(uri) {
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    return match ? this.#warms.get(match[1]) || null : null
  }

  async warm(uri) {
    this.#assertActive()
    const value = String(uri || '')
    const match = ASSET_URI_RE.exec(value)
    if (!match) return null
    const hash = match[1]
    const ext = match[2].toLowerCase()
    const existing = this.#warms.get(hash)
    if (existing) return existing
    const pending = this.#warmPending.get(hash)
    if (pending) return pending

    const task = (async () => {
      let entry = this.#entries.get(hash)
      if (!entry) {
        await this.#restoreLocal(hash, ext)
        entry = this.#entries.get(hash)
      }
      if (!entry) {
        try { await this.retry(value) } catch {}
        entry = this.#entries.get(hash)
      }

      const mime = String(entry?.mime || '').toLowerCase()
      const isVideo = mime.startsWith('video/') || VIDEO_EXTENSIONS.has(ext)
      const isImage = mime.startsWith('image/') || IMAGE_EXTENSIONS.has(ext)
      if (!isVideo && !isImage) return null

      if (isVideo) {
        const localPoster = this.#posters.get(hash)
        if (localPoster?.thumbhash) return this.#rememberWarm(hash, value, localPoster.thumbhash)

        const remotePoster = this.#freshVariant(entry, 'poster')
        if (remotePoster?.url) {
          try {
            const response = await fetch(remotePoster.url, { signal: this.#lifecycleController.signal })
            if (response.ok) {
              const generated = await this.#createWarm(await response.blob())
              if (generated) return this.#rememberWarm(hash, value, generated)
            }
          } catch {}
        }

        const poster = await this.poster(value)
        if (!poster) return null
        const generated = poster.thumbhash || (poster.blob ? await this.#createWarm(poster.blob) : null)
        return this.#rememberWarm(hash, value, generated)
      }

      let blob = this.#localFiles.get(hash) || null
      if (!blob) {
        const preview = this.#freshVariant(entry, '500')
        const source = preview?.url || entry?.url
        if (!source) return null
        try {
          const response = await fetch(source, { signal: this.#lifecycleController.signal })
          if (!response.ok) return null
          blob = await response.blob()
        } catch {
          return null
        }
      }
      return this.#rememberWarm(hash, value, await this.#createWarm(blob))
    })()
    this.#warmPending.set(hash, task)
    try { return await task } finally {
      if (this.#warmPending.get(hash) === task) this.#warmPending.delete(hash)
    }
  }

  async poster(uri) {
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return null
    const entry = this.#entries.get(match[1])
    const isVideo = VIDEO_EXTENSIONS.has(match[2].toLowerCase()) || String(entry?.mime || '').startsWith('video/')
    if (!isVideo) return null
    const existing = this.#posters.get(match[1])
    if (existing) return existing
    const remote = this.#freshVariant(entry, 'poster')
    if (remote) return remote
    if (!this.#posterPending.has(match[1])) {
      const task = (async () => {
        const source = this.resolve(uri)
        if (!source) return null
        const poster = await this.createPoster(source)
        this.#assertActive()
        if (poster) {
          this.#posters.set(match[1], poster)
          this.#rememberWarm(match[1], uri, poster.thumbhash || (poster.blob ? await this.#createWarm(poster.blob) : null))
          this.dispatchEvent(new CustomEvent('poster-ready', { detail: { hash: match[1], uri, poster } }))
          if (!this.readOnly && !entry?.local) {
            void this.#uploadDerived(match[1], [{ kind: 'poster', blob: poster.blob, width: poster.width, height: poster.height, mime: 'image/jpeg' }]).catch(() => {})
          }
        }
        return poster
      })()
      this.#posterPending.set(match[1], task)
      void task.finally(() => this.#posterPending.delete(match[1]))
    }
    return this.#posterPending.get(match[1])
  }

  async retry(uri, options = {}) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return null
    const hash = match[1]
    const ext = match[2].toLowerCase()
    const current = this.#entries.get(hash)
    const fresh = current?.url && (!current.expiresAt || current.expiresAt - Date.now() >= URL_REFRESH_SKEW)
    if (!options.force && fresh && !current.local) return current.url
    if (!this.#pending.has(hash)) {
      const file = this.#localFiles.get(hash) || await this.#restoreLocal(hash, ext)
      // Another caller can enter while IndexedDB is resolving. Re-check the
      // shared task before starting a duplicate signed-URL request/upload.
      if (!this.#pending.has(hash)) {
        if (file) this.#trackUpload(file, hash, ext)
        else {
          const task = (async () => {
            const result = await this.request('/sync/asset/resolve', {
              method: 'POST', body: JSON.stringify({ assets: [{ hash, ext }], variants: ['500', 'poster'] }),
            })
            const item = (result.assets || []).find(asset => asset.hash === hash)
            if (!item) throw new Error('asset_missing')
            return this.#cacheRemoteEntry(item, ext)?.url || null
          })()
          this.#pending.set(hash, task)
          void task.catch(() => {}).finally(() => {
            if (this.#pending.get(hash) === task) this.#pending.delete(hash)
          })
        }
      }
    }
    await this.#pending.get(hash)
    return this.#entries.get(hash)?.url || null
  }

  async prepareImport(file) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    if (!isSupportedCanvasFile(file)) throw new Error('web_canvas_file_unsupported')
    const ext = extensionFor(file)
    const warmTask = IMAGE_EXTENSIONS.has(ext) ? this.#createWarm(file) : Promise.resolve(null)
    const [hash, thumbhash] = await Promise.all([this.hash(file), warmTask])
    this.#assertActive()
    const uri = `asset://${hash}.${ext}`
    const objectUrl = URL.createObjectURL(file)
    this.#localFiles.set(hash, file)
    this.#entries.set(hash, { hash, ext, mime: file.type || null, url: objectUrl, expiresAt: null, local: true })
    this.#uncommitted.add(hash)
    const staged = {
      hash, ext, name: file.name, bytes: file.size, mime: file.type,
      status: 'uploading', ...(await localUploadPayload(file)),
      updatedAt: Date.now(),
    }
    this.#rememberWarm(hash, uri, thumbhash)
    return {
      uri, hash, ext, displayUrl: objectUrl, committed: false,
      thumbhash: this.#warms.get(hash) || null,
      assetRow: staged,
    }
  }

  async importFile(file, options = {}) {
    const prepared = await this.prepareImport(file)
    // Paid generation bytes join asset_uploads only inside the same multi-store
    // transaction as the Canvas, outbox and job. Until that transaction commits,
    // preparation is memory-only and must not start network/derived side effects.
    if (options.strictPersistence) {
      return {
        ...prepared,
        assetRow: { ...prepared.assetRow, deferredUpload: true },
      }
    }

    // Regular user imports retain the historical local-first behavior: cache
    // persistence is best-effort and upload/preview work starts immediately.
    try { await this.write('asset_uploads', prepared.assetRow) } catch {}
    this.#startPreparedImport(prepared)
    return prepared
  }

  async commitPreparedImport(value) {
    if (this.readOnly) throw environmentReadOnlyError()
    const supplied = value?.assetRow || value?.importedAssetRow || value
    const hash = String(supplied?.hash || '').toLowerCase()
    if (!HASH_RE.test(hash)) throw new Error('asset_prepared_import_invalid')

    // This read is the activation gate. A strict import may only acquire
    // upload/derivation side effects after commitGenerationDelivery made its
    // asset_uploads row durable in the paid-delivery transaction.
    const persisted = await this.read('asset_uploads', hash)
    const blob = persisted?.localBlob instanceof Blob
      ? persisted.localBlob
      : persisted?.localBytes instanceof ArrayBuffer
        ? new Blob([persisted.localBytes], { type: persisted.mime || 'application/octet-stream' })
        : null
    if (!blob) throw new Error('asset_prepared_import_not_committed')

    const ext = String(persisted.ext || supplied.ext || '').toLowerCase()
    if (!IMAGE_EXTENSIONS.has(ext) && !VIDEO_EXTENSIONS.has(ext)) throw new Error('asset_prepared_import_invalid')
    let file = this.#localFiles.get(hash)
    if (!file) {
      file = typeof File === 'function'
        ? new File([blob], persisted.name || `asset.${ext}`, { type: persisted.mime || blob.type })
        : blob
      this.#localFiles.set(hash, file)
    }
    const uri = `asset://${hash}.${ext}`
    let entry = this.#entries.get(hash)
    if (!entry?.local) {
      entry = {
        ...(entry || {}), hash, ext, mime: persisted.mime || file.type || null,
        url: URL.createObjectURL(file), expiresAt: null, local: true,
      }
      this.#entries.set(hash, entry)
    }
    this.#uncommitted.add(hash)
    this.#rememberWarm(hash, uri, value?.thumbhash || value?.importedThumbhash)

    if (persisted.status === 'committed') {
      this.#uncommitted.delete(hash)
      if (VIDEO_EXTENSIONS.has(ext)) void this.poster(uri)
      else this.#queueDerived(hash, ext)
      return { uri, hash, ext, displayUrl: entry.url, committed: true, started: false }
    }
    if (persisted.status !== 'uploading') {
      await this.write('asset_uploads', {
        ...persisted, status: 'uploading', session: undefined, error: undefined, updatedAt: Date.now(),
      })
    }
    return this.#startPreparedImport({ uri, hash, ext, displayUrl: entry.url })
  }

  #startPreparedImport(prepared) {
    this.#assertActive()
    const { uri, hash, ext, displayUrl } = prepared
    const file = this.#localFiles.get(hash)
    if (!file) throw new Error('asset_prepared_import_bytes_missing')
    this.#notifyUpload(hash, 'uploading', { uri })
    const upload = this.#trackUpload(file, hash, ext, { uri, objectUrl: displayUrl }, true)
    if (VIDEO_EXTENSIONS.has(ext)) void this.poster(uri)
    else this.#queueDerived(hash, ext)
    return upload
  }

  async #upload(file, hash, ext, existing = {}, staged = false) {
    this.#assertActive()
    const uri = existing.uri || `asset://${hash}.${ext}`
    const objectUrl = existing.objectUrl || this.#entries.get(hash)?.url
    const controller = new AbortController()
    const merged = mergeAbortSignals(controller.signal, this.#lifecycleController.signal)
    this.#controllers.set(hash, controller)
    if (!staged) {
      try {
        await this.write('asset_uploads', {
          hash, ext, name: file.name, bytes: file.size, mime: file.type,
          status: 'uploading', ...(await localUploadPayload(file)),
          updatedAt: Date.now(),
        })
      } catch {}
    }
    try {
      if (file.size <= SMALL_UPLOAD_LIMIT) await uploadSingle(file, hash, this.request, merged.signal)
      else await uploadMultipart(file, hash, this.request, session => this.write('asset_uploads', { hash, ext, name: file.name, bytes: file.size, mime: file.type, status: 'uploading', session, updatedAt: Date.now() }), merged.signal)
      this.#assertActive()
      try {
        // Merge, don't replace: the staged row carries localBlob/localBytes and
        // (by now, possibly) previewVariants. A bare rewrite here destroyed
        // them at the exact moment the upload committed — after which every
        // reopen of the canvas re-downloaded bytes this device had just held.
        // Local bytes are forever (same policy as the mirror path above).
        const previous = await this.read('asset_uploads', hash).catch(() => null)
        const payload = (previous?.localBlob || previous?.localBytes) ? {} : await localUploadPayload(file)
        const committed = { ...(previous || {}) }
        delete committed.deferredUpload
        await this.write('asset_uploads', {
          ...committed, ...payload,
          hash, ext, name: file.name, bytes: file.size, mime: file.type,
          status: 'committed', session: undefined, error: undefined, updatedAt: Date.now(),
        })
      } catch {}
      this.#uncommitted.delete(hash)
      this.#notifyUpload(hash, 'committed', { uri })
      void this.#ensureDerived(hash, ext, file).catch(() => {})
      return { uri, hash, ext, displayUrl: objectUrl, committed: true }
    } catch (error) {
      const status = merged.signal.aborted ? 'cancelled' : 'failed'
      if (!this.#disposed) {
        let previous = null
        try { previous = await this.read('asset_uploads', hash) } catch {}
        try {
          await this.write('asset_uploads', { ...previous, hash, ext, name: file.name, bytes: file.size, mime: file.type, status, error: String(error?.message || error), updatedAt: Date.now() })
        } catch {}
      }
      this.#notifyUpload(hash, status, { uri, error })
      throw error
    } finally {
      merged.dispose()
      this.#controllers.delete(hash)
    }
  }

  #queueDerived(hash, ext) {
    if (this.readOnly || this.#disposed) return
    if (!HASH_RE.test(String(hash)) || this.#derivationPending.has(hash) || this.#derivationQueued.has(hash)) return
    this.#derivationQueued.add(hash)
    this.#derivationQueue.push({ hash, ext })
    this.#scheduleDerivationDrain()
  }

  #scheduleDerivationDrain() {
    if (this.#disposed || this.#derivationIdleHandle || !this.#derivationQueue.length) return
    const run = () => {
      this.#derivationIdleHandle = 0
      if (!this.#disposed) this.#drainDerivationQueue()
    }
    if (this.scheduleIdle) {
      this.#derivationIdleHandle = this.scheduleIdle(run) || 1
      return
    }
    if (typeof requestIdleCallback === 'function') {
      // Preview backfill is opportunistic and must yield to real interaction.
      // No timeout: a busy page should stay responsive instead of forcing the
      // next full-size decode after an arbitrary deadline.
      this.#derivationIdleHandle = requestIdleCallback(run)
    } else {
      const timer = setTimeout(run, 1_000)
      timer.unref?.()
      this.#derivationIdleHandle = timer
    }
  }

  #drainDerivationQueue() {
    if (this.#disposed) return
    while (this.#derivationActive < 1 && this.#derivationQueue.length) {
      const task = this.#derivationQueue.shift()
      this.#derivationQueued.delete(task.hash)
      this.#derivationActive += 1
      void this.#ensureDerived(task.hash, task.ext).catch(() => {}).finally(() => {
        this.#derivationActive -= 1
        if (!this.#disposed) this.#scheduleDerivationDrain()
      })
    }
  }

  async #ensureDerived(hash, ext, sourceFile = null) {
    this.#assertActive()
    const current = this.#derivationPending.get(hash)
    if (current) return current
    const task = (async () => {
      const entry = this.#entries.get(hash)
      // Local sources only. Remote (cloud-synced) assets already have
      // server-side '500'/'poster' variants from the resolve batch, and their
      // presigned R2 URLs reject browser fetch/canvas reads (no bucket CORS,
      // tainted-canvas) — attempting derivation just floods the console.
      if (!sourceFile && !entry?.local && !this.#localFiles.has(hash)) return
      const normalizedExt = String(ext || entry?.ext || '').toLowerCase()
      const mime = String(sourceFile?.type || entry?.mime || '').toLowerCase()
      const isVideo = mime.startsWith('video/') || VIDEO_EXTENSIONS.has(normalizedExt)
      const isImage = mime.startsWith('image/') || IMAGE_EXTENSIONS.has(normalizedExt)
      if (isVideo) {
        if (this.#freshVariant(entry, 'poster')) return
        const uri = `asset://${hash}.${normalizedExt || 'mp4'}`
        let poster = this.#posters.get(hash)
        if (!poster) {
          const source = sourceFile ? URL.createObjectURL(sourceFile) : entry?.url
          if (!source) return
          try { poster = await this.createPoster(source) } finally { if (sourceFile) URL.revokeObjectURL(source) }
          this.#assertActive()
          if (poster) {
            this.#posters.set(hash, poster)
            this.#rememberWarm(hash, uri, poster.thumbhash || (poster.blob ? await this.#createWarm(poster.blob) : null))
            this.dispatchEvent(new CustomEvent('poster-ready', { detail: { hash, uri, poster } }))
          }
        }
        if (poster?.blob && !this.#uncommitted.has(hash)) await this.#uploadDerived(hash, [{ kind: 'poster', blob: poster.blob, width: poster.width, height: poster.height, mime: 'image/jpeg' }])
        return
      }
      if (!isImage) return
      const fresh500 = this.#freshVariant(entry, '500')
      if (fresh500 && !fresh500.local) return
      let variants = null
      if (!fresh500) {
        let blob = sourceFile || this.#localFiles.get(hash) || null
        if (!blob && entry?.url) {
          const response = await fetch(entry.url, { signal: this.#lifecycleController.signal })
          if (!response.ok) throw new Error(`asset_variant_source_${response.status}`)
          blob = await response.blob()
        }
        if (!blob) return
        variants = await this.createVariants(blob)
        this.#assertActive()
        // Derived previews must survive without the sync backend: keep local
        // object URLs (blobs retained so a later commit can still upload),
        // and only talk to /sync once the original has committed.
        this.#retainLocalVariants(hash, variants)
      } else {
        variants = Object.entries(entry?.variants || {})
          .filter(([, value]) => value?.local && value.blob)
          .map(([kind, value]) => ({ kind, blob: value.blob, width: value.width, height: value.height, mime: value.mime }))
      }
      if (variants?.length && !this.#uncommitted.has(hash)) await this.#uploadDerived(hash, variants)
    })()
    this.#derivationPending.set(hash, task)
    try { return await task } finally {
      this.#derivedOnce.add(hash)
      if (this.#derivationPending.get(hash) === task) this.#derivationPending.delete(hash)
    }
  }

  #retainLocalVariants(hash, variants) {
    if (!variants?.length) return
    const entry = this.#entries.get(hash)
    if (!entry) return
    const mapped = { ...(entry.variants || {}) }
    const retained = []
    for (const variant of variants) {
      if (!variant?.blob || mapped[variant.kind]?.url) continue
      mapped[variant.kind] = {
        url: URL.createObjectURL(variant.blob),
        blob: variant.blob,
        width: variant.width,
        height: variant.height,
        mime: variant.mime,
        local: true,
      }
      retained.push(variant.kind)
    }
    if (!retained.length) return
    entry.variants = mapped
    this.#entries.set(hash, entry)
    for (const kind of retained) {
      this.dispatchEvent(new CustomEvent('asset-variant-ready', {
        detail: { hash, kind, variant: mapped[kind] },
      }))
    }
    // Previews outlive the tab: persisted next to the original's localBlob so
    // a reload restores them instead of re-deriving (and re-flashing).
    void this.#persistLocalVariants(hash).catch(() => {})
  }

  async #persistLocalVariants(hash) {
    this.#assertActive()
    const entry = this.#entries.get(hash)
    if (!entry?.variants) return
    const previews = {}
    for (const [kind, variant] of Object.entries(entry.variants)) {
      if (variant?.local && variant.blob) previews[kind] = { blob: variant.blob, width: variant.width, height: variant.height, mime: variant.mime }
    }
    if (!Object.keys(previews).length) return
    const upload = await this.read('asset_uploads', hash)
    if (!upload) return
    await this.write('asset_uploads', { ...upload, previewVariants: previews, updatedAt: Date.now() })
  }

  async #uploadDerived(hash, variants) {
    this.#assertActive()
    if (!variants?.length) return
    const entry = this.#entries.get(hash)
    if (!entry) return
    const results = await Promise.allSettled(variants.map(async variant => ({
      kind: variant.kind,
      value: await uploadVariant(variant, hash, this.request, this.#lifecycleController.signal),
    })))
    this.#assertActive()
    const mapped = { ...(entry.variants || {}) }
    const ready = []
    for (const result of results) {
      if (result.status !== 'fulfilled') continue
      const previous = mapped[result.value.kind]
      if (previous?.local && previous.url?.startsWith('blob:')) URL.revokeObjectURL(previous.url)
      // Commit responds with an expiring presigned GET; swap to the stable CDN
      // form immediately so in-session URLs match what the next resolve returns.
      const stable = this.#rewriteVariantsToCdn({ [result.value.kind]: result.value.value })[result.value.kind]
      mapped[result.value.kind] = stable
      ready.push({ kind: result.value.kind, value: stable })
    }
    entry.variants = mapped
    this.#entries.set(hash, entry)
    for (const result of ready) {
      this.dispatchEvent(new CustomEvent('asset-variant-ready', {
        detail: { hash, kind: result.kind, variant: result.value },
      }))
    }
  }

  async cancel(uri) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return false
    this.#controllers.get(match[1])?.abort()
    const upload = await this.read('asset_uploads', match[1])
    if (upload?.session?.uploadId) {
      await this.request('/sync/asset/multipart/abort', { method: 'DELETE', body: JSON.stringify({ hash: match[1], uploadId: upload.session.uploadId }) }).catch(() => {})
    }
    return true
  }

  async allCommitted(document) {
    this.#assertActive()
    return (await this.commitState(document)) === 'committed'
  }

  async commitState(document) {
    this.#assertActive()
    let state = 'committed'
    for (const { hash } of scanAssetUris(document)) {
      let upload = null
      try { upload = await this.read('asset_uploads', hash) } catch { return 'uploading' }
      if (upload?.status === 'failed' || upload?.status === 'cancelled') return upload.status
      if (this.#uncommitted.has(hash) || (upload && upload.status !== 'committed')) state = 'uploading'
    }
    return state
  }

  async download(uri, filename) {
    this.#assertActive()
    const url = this.resolve(uri) || await this.retry(uri)
    if (!url) throw new Error('asset_missing')
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename || uri.slice(8)
    anchor.rel = 'noopener'
    anchor.click()
  }

  #notifyUpload(hash, status, extra = {}) {
    if (this.#disposed) return
    this.dispatchEvent(new CustomEvent('upload-status', { detail: { hash, status, ...extra } }))
  }

  releaseAll() {
    if (this.#disposed) return
    this.#disposed = true
    this.#lifecycleController.abort(new DOMException('Asset map disposed', 'AbortError'))
    if (this.#mirrorTimer != null) clearTimeout(this.#mirrorTimer)
    this.#mirrorTimer = null
    this.#mirrorQueue.length = 0
    for (const timer of this.#refreshTimers.values()) clearTimeout(timer)
    this.#refreshTimers.clear()
    for (const entry of this.#entries.values()) {
      if (entry.local && entry.url?.startsWith('blob:')) URL.revokeObjectURL(entry.url)
      for (const variant of Object.values(entry.variants || {})) {
        if (variant?.local && variant.url?.startsWith('blob:')) URL.revokeObjectURL(variant.url)
      }
    }
    this.#entries.clear()
    this.#uncommitted.clear()
    for (const poster of this.#posters.values()) if (poster.url?.startsWith('blob:')) URL.revokeObjectURL(poster.url)
    this.#posters.clear()
    this.#posterPending.clear()
    this.#warms.clear()
    this.#warmPending.clear()
    this.#derivationPending.clear()
    this.#derivationQueue.length = 0
    this.#derivationQueued.clear()
    this.#derivedOnce.clear()
    this.#mirrorSeen.clear()
    if (this.#derivationIdleHandle) {
      clearTimeout(this.#derivationIdleHandle)
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this.#derivationIdleHandle)
      this.#derivationIdleHandle = 0
    }
    for (const controller of this.#controllers.values()) controller.abort()
    this.#controllers.clear()
  }
}
